import { useEffect, useCallback } from 'react';

/**
 * Keyboard shortcuts for document and master pages.
 * - Alt+N: Create new record (works in list view)
 * - Ctrl+S: Save/Post record (works in form view)
 * - Escape: Go back to list view (works in form/view mode)
 *
 * @param {Object} options
 * @param {Function} options.onNew - Called on Alt+N (e.g., handleCreate)
 * @param {Function} options.onSave - Called on Ctrl+S (e.g., () => handleSave('Posted'))
 * @param {string} options.viewMode - Current view mode: 'list' | 'form' | 'view'
 * @param {boolean} [options.saving] - If true, Ctrl+S is disabled (prevent double-save)
 */
const useDocumentShortcuts = ({ onNew, onSave, viewMode, saving = false }) => {
  const handleKeyDown = useCallback((e) => {
    // Alt+N: New record (Alt doesn't conflict with browser shortcuts)
    if (e.altKey && e.key === 'n') {
      e.preventDefault();
      if (viewMode === 'list' && onNew) {
        onNew();
      }
    }

    // Ctrl+S or Cmd+S: Save/Post
    if ((e.ctrlKey || e.metaKey) && e.key === 's') {
      e.preventDefault();
      if (viewMode === 'form' && onSave && !saving) {
        onSave();
      }
    }
  }, [onNew, onSave, viewMode, saving]);

  useEffect(() => {
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [handleKeyDown]);
};

export default useDocumentShortcuts;
